import type { ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconFileText, IconLink, IconMapPin } from "@tabler/icons-react";
import { useApiQuery, type Api } from "../../services/api";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import type { SupportedLanguage } from "../../interfaces";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import {
  gaEventCongressPdfCardOpened,
  gaEventCongressSessionsCardViewed,
  gaEventCongressSessionsOpened,
  type CongressAnalyticsContext,
} from "../../tools/analytics/domains/congress.analytics";
import ArticlePublicationCard from "../cards/ArticlePublicationCard";
import InfographicCard from "../cards/InfographicCard";
import PlaylistCard from "../cards/PlaylistCard";
import PodcastCard from "../cards/PodcastCard";
import VideoCard from "../cards/VideoCard";
import { CongressContentCard } from "./CongressContentCard";
import { CongressImageCard } from "./CongressImageCard";
import { thumbnailSrc } from "../../domains/congress/documentImage";
import { CongressTopSessionsCard } from "./CongressTopSessionsCard";
import {
  CongressAttributionProvider,
  useCongressAttribution,
} from "../../domains/congress/congressAttribution";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useShareArticle } from "../../domains/user/hooks/useShareArticle";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useLoadedAudioId } from "../../domains/app/contexts/audio.context";
import { formatArticleDate } from "../../domains/app/utils/renderDate";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../domains/app/utils/getArticleTypeText";
import { authorLine } from "../../domains/article/utils/authorLine";
import { gaEventSelectItemArticle } from "../../tools/analytics/domains/article.analytics";
import { DesktopArticleRow } from "../discovery/DesktopArticleRow";
import { DesktopSaveButton } from "../save/DesktopSaveButton";
import { DesktopCollectionCard } from "../cards/DesktopCollectionCard";
import { DesktopCongressDocCard } from "../congress/DesktopCongressDocCard";

const FALLBACK_ICON_CLASS = "size-8 stroke-[1.5px]";

// Congress blocks live inside the Resources tab but are their own surface: the
// list name is what tells the reused content events (content_viewed, _liked,
// _saved, _shared, article_listened, …) that the interaction happened on a
// congress, which is how they land in `navigation_context = conferences`.
// ROOM_CONGRESSES is the legacy Conferences page — a different surface again.
const ANALYTICS_LIST_NAME = AnalyticsItemList.CONGRESS_SPACE;

type ContentBlock =
  Api["get"]["/webapp/private/congress-spaces/:id/content"]["output"]["body"][number];
type PdfDetailsIcon = Extract<ContentBlock, { kind: "pdfCard" }>["detailsIcon"];

function pdfDetailIcon(icon: PdfDetailsIcon): ReactNode {
  if (icon === "location")
    return <IconMapPin className="size-4 stroke-[1.5px]" />;
  if (icon === "link") return <IconLink className="size-4 stroke-[1.5px]" />;
  return <IconFileText className="size-4 stroke-[1.5px]" />;
}

type PdfCardBlock = Extract<ContentBlock, { kind: "pdfCard" }>;

/**
 * Where the space's cards are drawn. `phone`: the mobile Resources tab, where a
 * PDF opens its own page. `desktop-tab`: the congress chip of the desktop
 * Resources tab, where a PDF opens the space page's viewer. `desktop-page`: the
 * space page itself, which opens its viewer and draws the Top sessions as a
 * section of its own, so no Top sessions card here.
 */
type ContentSurface =
  | { kind: "phone" }
  | { kind: "desktop-tab"; onOpenDocument: (block: PdfCardBlock) => void }
  | { kind: "desktop-page"; onOpenDocument: (block: PdfCardBlock) => void };

function pdfTapHint(icon: PdfDetailsIcon, t: (key: string) => string): string {
  if (icon === "location") return t("Tap to see the map");
  if (icon === "link") return t("Tap to open");
  return t("Tap to read");
}

// The desktop card's action, after the detail ("Booth #35 · Open the floorplan").
function desktopDocumentHint(
  block: PdfCardBlock,
  t: (key: string) => string
): string {
  if (block.documentKind === "image") return t("Open the image");
  return block.detailsIcon === "location"
    ? t("Open the floorplan")
    : t("Open in the document viewer");
}

/**
 * The desktop card of a congress document (Figma DS "CongressDocCard"), in the
 * space's grid or its rail. A PDF's cover is its thumbnail or its first page.
 */
function DesktopDocumentCard({
  block,
  place,
  onOpen,
}: {
  block: PdfCardBlock;
  place: "grid" | "rail";
  onOpen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const hint = desktopDocumentHint(block, t);
  const meta = {
    text:
      block.details === null || block.details === ""
        ? hint
        : `${block.details} · ${hint}`,
    place: block.detailsIcon === "location",
  };
  switch (block.documentKind) {
    case "pdf":
      return (
        <DesktopCongressDocCard
          label={`${block.labelType} · PDF`}
          title={block.documentTitle}
          meta={meta}
          cover={{
            kind: "pdf",
            thumbnailUrl: block.thumbnailUrl,
            thumbnailSizes: block.thumbnailSizes,
            pdfUrl: block.pdfUrl,
          }}
          onOpen={onOpen}
        />
      );
    case "image":
      return (
        <DesktopCongressDocCard
          label={block.labelType}
          title={block.documentTitle}
          meta={meta}
          cover={{
            kind: "image",
            imageUrl: block.imageUrl,
            imageSizes: block.imageSizes,
            crop: block.crop,
            layoutId: `congress-document-${place}-${block.id}`,
          }}
          onOpen={onOpen}
        />
      );
    default:
      throw new Error(block.documentKind satisfies never);
  }
}

/**
 * A congress document as its card (Figma DS "CongressDocCard"): a PDF is the
 * labelled card, an image the picture that opens its own lightbox. A congress
 * document is a PDF or an image. THIS IS THE ONLY PLACE THAT KNOWS how each
 * is drawn; `onOpen` is the caller's (analytics, and where a PDF opens).
 * Deliberately NOT driven by an "action" field on the payload: the wire
 * describes data, not UI behaviour.
 */
export function CongressDocumentCard({
  block,
  accentColor,
  place,
  onOpen,
}: {
  block: PdfCardBlock;
  accentColor: string;
  /**
   * Where the card sits: the phone's feed, or the desktop grid or rail. The
   * space page can draw one image in its grid and in its rail: each copy keeps
   * its own lightbox morph.
   */
  place: "phone" | "desktop-grid" | "desktop-rail";
  onOpen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (place === "desktop-grid")
    return <DesktopDocumentCard block={block} place="grid" onOpen={onOpen} />;
  if (place === "desktop-rail")
    return <DesktopDocumentCard block={block} place="rail" onOpen={onOpen} />;
  switch (block.documentKind) {
    case "pdf":
      return (
        <CongressContentCard
          tone="light"
          accentColor={accentColor}
          label={block.labelType}
          title={block.documentTitle}
          tapHint={pdfTapHint(block.detailsIcon, t)}
          detail={block.details}
          detailIcon={pdfDetailIcon(block.detailsIcon)}
          imageUrl={
            block.thumbnailUrl === null
              ? null
              : thumbnailSrc(block.thumbnailUrl, block.thumbnailSizes)
          }
          fallbackIcon={<IconFileText className={FALLBACK_ICON_CLASS} />}
          onClick={onOpen}
        />
      );
    case "image":
      return (
        <CongressImageCard
          imageUrl={block.imageUrl}
          imageSizes={block.imageSizes}
          crop={block.crop}
          label={block.labelType}
          title={block.documentTitle}
          layoutId={`congress-document-phone-${block.id}`}
          onOpen={onOpen}
        />
      );
    default:
      throw new Error(block.documentKind satisfies never);
  }
}

export function CongressSpaceContent({
  spaceId,
  analytics,
  language,
  roomId,
  accentColor,
  shortName,
  curatorName,
  curatorRole,
  curatorAvatarUrl,
  topSessionsCount,
  topSessionsCardTitle,
  surface,
}: {
  spaceId: string;
  /** Congress identity every event fired from this subtree carries */
  analytics: CongressAnalyticsContext;
  /** Content language of the reader — the payload is scoped to it. */
  language: SupportedLanguage;
  roomId: string;
  accentColor: string;
  shortName: string;
  curatorName: string | null;
  curatorRole: string;
  curatorAvatarUrl: string | null;
  topSessionsCount: number;
  /** Admin-authored heading for `language`; null → the app's own label */
  topSessionsCardTitle: string | null;
  surface: ContentSurface;
}) {
  const history = useHistory();
  const result = useApiQuery("/webapp/private/congress-spaces/:id/content", {
    param: { id: spaceId },
    query: { language },
  });
  const blocks = result.state === "success" ? result.body : [];
  const hasTopSessionsBlock = blocks.some(
    (block) => block.kind === "topSessions"
  );

  // The card is drawn either where the admin placed the block or, failing that,
  // appended below — both paths hinge on there being sessions in this language.
  const showsTopSessionsCard =
    topSessionsCount > 0 && surface.kind !== "desktop-page";
  useEffectOnce(() => {
    if (!showsTopSessionsCard) return;
    gaEventCongressSessionsCardViewed({
      context: analytics,
      sessionsCount: topSessionsCount,
    });
  }, [showsTopSessionsCard, topSessionsCount]);

  const openTopSessions = () => {
    gaEventCongressSessionsOpened({
      context: analytics,
      sessionsCount: topSessionsCount,
    });
    // On desktop the Top sessions are a section of the space's page; the
    // phone has a page of their own.
    if (surface.kind === "phone") {
      history.push(`/room/congress/${roomId}/${spaceId}/top-sessions`);
      return;
    }
    history.push({
      pathname: `/room/congress/${roomId}/${spaceId}`,
      state: { congressEntryPoint: "chip" },
    });
  };
  const topSessionsCard = (
    <CongressTopSessionsCard
      cardTitle={topSessionsCardTitle}
      contentLang={language}
      shortName={shortName}
      curatorName={curatorName}
      curatorRole={curatorRole}
      curatorAvatarUrl={curatorAvatarUrl}
      sessionCount={topSessionsCount}
      onOpen={openTopSessions}
    />
  );
  // NOT RoomListWrapper (the room-sections list below): its `*:py-3` is padding
  // on each child, not spacing between them. Bare publication rows absorb it as
  // breathing room, but a self-contained card (PDF, collection, top sessions)
  // takes it INSIDE its rounded box — inflating it and crushing its content.
  // Blocks stay direct children of the parent's flex gap, evenly spaced.
  //
  // Every card below navigates to a page that fires its OWN `view_item` (which
  // becomes `playlist_opened` / `content_viewed` in Mixpanel). Those pages read
  // only the room and the content id from their URL, so without this the open
  // is indistinguishable from the same content opened anywhere else in the
  // room. The provider makes the congress identity available to the cards, and
  // each card stamps it onto the navigation it already performs.
  //
  // The provider's subtree is WIDER than this surface — the cards render their
  // own PlaylistPanel, which renders content cards back — so reading it is
  // gated on the list name. See `useCongressAttribution`.
  return (
    <CongressAttributionProvider attribution={analytics}>
      {blocks.map((block, index) => {
        switch (block.kind) {
          // A congress document is a PDF or an image. THIS IS THE ONLY PLACE
          // THAT KNOWS: one switch decides both the card body and what tapping
          // does, so the destination lives beside the rendering instead of in a
          // separate component. Everything upstream — ordering, the language
          // scope, the label, the wire mapper — stays kind-agnostic, so a third
          // kind is one branch here plus the schema.
          //
          // Deliberately NOT driven by an "action" field on the payload: the
          // wire describes data, not UI behaviour, and two kinds do not justify
          // that indirection.
          case "pdfCard":
            return (
              <CongressDocumentCard
                key={block.id}
                block={block}
                accentColor={accentColor}
                place={surface.kind === "phone" ? "phone" : "desktop-grid"}
                onOpen={() => {
                  // On the space page a PDF opens through the page's viewer,
                  // which reports the open itself: reported once.
                  if (
                    surface.kind !== "desktop-page" ||
                    block.documentKind === "image"
                  )
                    gaEventCongressPdfCardOpened({
                      context: analytics,
                      cardId: block.id,
                      cardLabel: block.labelType,
                      position: index,
                    });
                  // An image opens its own lightbox: the call is analytics
                  // only. A PDF opens where the surface says.
                  if (block.documentKind === "image") return;
                  switch (surface.kind) {
                    case "phone":
                      // NOT window.open: the native shells load raw PDF URLs
                      // in the main webview (edge-to-edge, no safe-area, no
                      // back).
                      history.push(
                        `/room/congress/${roomId}/${spaceId}/pdf/${block.id}`
                      );
                      return;
                    case "desktop-tab":
                    case "desktop-page":
                      // The desktop viewer opens over the surface itself:
                      // no navigation, no second view of the space.
                      surface.onOpenDocument(block);
                      return;
                    default:
                      throw new Error(surface satisfies never);
                  }
                }}
              />
            );
          case "thematicCollection":
            return surface.kind !== "phone" ? (
              // Figma "CollectionCard": the desktop cover, title and counts.
              // A partner's collection has no bookmark.
              <DesktopCollectionCard
                key={block.id}
                playlist={{
                  id: block.playlistId,
                  title: block.title,
                  images:
                    block.imageUrl === null
                      ? []
                      : [{ url: block.imageUrl, sizes: null }],
                  metrics: {
                    articles: block.articleCount,
                    journals: block.journalCount,
                  },
                  featuredArticle: block.featuredArticle,
                }}
                onOpen={() => {
                  history.push({
                    pathname: `/room/playlist/${roomId}/${block.playlistId}`,
                    state: { congressAttribution: analytics },
                  });
                }}
                bookmark={null}
              />
            ) : (
              <PlaylistCard
                key={block.id}
                variant="in-room"
                primaryColor={accentColor}
                secondaryColor={accentColor}
                playlist={{
                  id: block.playlistId,
                  title: block.title,
                  // The admin-authored overline, forwarded verbatim. This used
                  // to hardcode the generic label, so an expert-attributed
                  // collection ("Sélection du Pr X") read the same as a plain
                  // thematic one. PlaylistCard owns the generic fallback now,
                  // so passing null here still prints "Collection".
                  headerTitle: block.headerTitle,
                  images:
                    block.imageUrl === null
                      ? []
                      : [{ url: block.imageUrl, sizes: null }],
                  metrics: { articles: block.articleCount },
                  // LOAD-BEARING null — do not "simplify" this by passing the
                  // API's playlist object through. `PlaylistCard` resolves its
                  // tint as `playlist.room.secondaryColor` FIRST and only falls
                  // back to the `in-room` colour above, so a populated `room`
                  // makes the ROOM's brand colour win and the congress tint
                  // silently reverts on every collection card in the space.
                  //
                  // This looks like an oversight because it is the opposite of
                  // the pattern everywhere else: eight other surfaces hand
                  // PlaylistCard the whole API object (see #1394). Here that
                  // would be wrong — the congress owns the tint, not the room.
                  room: null,
                }}
                onClick={() => {
                  // Stamped here rather than read from the provider: this card
                  // owns its own navigation (every other kind pushes from
                  // inside the card component), so the congress identity is
                  // already in scope and there is nothing ambient to gate.
                  history.push({
                    pathname: `/room/playlist/${roomId}/${block.playlistId}`,
                    state: { congressAttribution: analytics },
                  });
                }}
              />
            );
          // The block is placed once and serves every language; it disappears
          // when the curator has no session filed in the reader's language.
          case "topSessions":
            return !showsTopSessionsCard ? null : (
              <div key={block.id}>{topSessionsCard}</div>
            );
          case "article":
            // On desktop a publication is the feed's article line, across the
            // two columns (Figma "Top 3 du jour · DesktopArticleRow").
            return surface.kind !== "phone" ? (
              <div key={block.id} className="col-span-2">
                <CongressArticleRow roomId={roomId} article={block.article} />
              </div>
            ) : (
              <ArticlePublicationCard
                key={block.id}
                roomId={roomId}
                article={block.article}
                analyticsListName={ANALYTICS_LIST_NAME}
                recommendation={null}
                onClick={null}
                className={null}
                showImage={true}
              />
            );
          case "video":
            return (
              <VideoCard
                key={block.id}
                roomId={roomId}
                video={block.content}
                analyticsListName={ANALYTICS_LIST_NAME}
                recommendation={null}
                onClick={null}
                className={null}
              />
            );
          case "infographic":
            return (
              <InfographicCard
                key={block.id}
                roomId={roomId}
                infographic={block.content}
                analyticsListName={ANALYTICS_LIST_NAME}
                recommendation={null}
                onClick={null}
                className={null}
              />
            );
          case "podcast":
            return (
              <PodcastCard
                key={block.id}
                roomId={roomId}
                podcast={block.podcast}
                listIndex={index}
                analyticsListName={ANALYTICS_LIST_NAME}
                recommendation={null}
                onClick={null}
                className={null}
              />
            );
          default:
            throw new Error(block satisfies never);
        }
      })}
      {!hasTopSessionsBlock && showsTopSessionsCard && topSessionsCard}
    </CongressAttributionProvider>
  );
}

type CongressArticle = Extract<ContentBlock, { kind: "article" }>["article"];

/**
 * An article of the space, as every desktop feed draws one: the desktop save
 * popover under its bookmark, never the phone's collection sheet. Opening it
 * carries the congress attribution the article page reports.
 */
function CongressArticleRow({
  roomId,
  article,
}: {
  roomId: string;
  article: CongressArticle;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  // Congress text follows the payload's language (custom/congress-content-lang-only).
  const { contentLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const { articleLink } = usePageLinks(roomId);
  const { triggerShare } = useShareArticle(ANALYTICS_LIST_NAME);
  const congressAttribution = useCongressAttribution(ANALYTICS_LIST_NAME);
  const loadedAudioId = useLoadedAudioId();
  const journal = article.journal;
  const cardImage = article.cardImage;
  return (
    <DesktopArticleRow
      onRemove={null}
      layout="row"
      title={article.title}
      author={authorLine(article.primary_author, article.authors)}
      description={null}
      date={formatArticleDate(article.publication_date, contentLang)}
      readingMinutes={null}
      meta={
        article.article_type === null
          ? t("Article")
          : getArticleTypeText(article.article_type)
      }
      metaVariant={getArticleTypeVariant(article.article_type)}
      access={article.isOpenAccess === true ? "open" : "closed"}
      imageUrl={
        cardImage === null
          ? null
          : cardImage.sizes === null
            ? cardImage.url
            : cardImage.sizes.medium.file
      }
      isInPlayer={loadedAudioId === article.id}
      bookmark={
        <DesktopSaveButton
          article={article}
          analyticsListName={ANALYTICS_LIST_NAME}
          size="row"
        />
      }
      onShare={
        currentUser === null
          ? null
          : () => {
              void triggerShare(article, currentUser);
            }
      }
      onClick={() => {
        gaEventSelectItemArticle({ article, listName: ANALYTICS_LIST_NAME });
        history.push({
          pathname: articleLink(article),
          state: { congressAttribution },
        });
      }}
      // The space's payload carries no impact factor.
      source={
        journal === null
          ? null
          : {
              kind: "journal",
              name: journal.name,
              impactFactor: null,
              onClick: () => {
                history.push(`/journal/${journal.uid}`);
              },
            }
      }
    />
  );
}
